import PptxGenJS, { Slide } from "pptxgenjs"
import { ReportConfiguration } from "../configuration"
import {
  getMasterSlideBaseOptions,
  getMasterSlideDefaultOptions,
  getMasterSlideTableOptions,
  MasterSlide,
  getMasterSlideTableWithSubtitleOptions,
  getMasterSlideChartOptions,
  getMasterSlideInfoOptions,
} from "./pptx-configuration/master-slides"
import { Placeholder } from "./pptx-configuration/placeholders"
import * as DebtGenerator from "./debt-generator"
import * as budgetAnnexGenerator from "./budgetGenerator"
import { checkPpiSection, createPpiSection } from "./ppi-generator"
import {
  checkObservationSection,
  createObservationSection,
} from "./observation-generator"
import {
  createGlossaireSection,
} from "./glossaryGenerator"
import { DocumentSection } from "./types"
import { toInches, formatTitle } from "./utils"
import * as Constants from "./pptx-configuration/constants"
import * as Model from "../../../model"
import * as DobSection from "./dob-generator"
export async function generate(
  configuration: ReportConfiguration,
  localAuthority: Model.LocalAuthority,
  decomposedLoans: Model.LoanWithDecomposition[],
  scenario: Model.Scenario,
  ppi: Model.Ppi,
  budgetData: any,
  childDecomposedLoans: any,
  loans: any,
  propsloan: any,
  gabarit:string
): Promise<PptxGenJS> {
	const pptx = new PptxGenJS()  

	pptx.setLayout(Constants.LAYOUT)
	pptx.setTitle(configuration.generalities.title || Constants.DEFAULT_DOC_TITLE)
	pptx.setSubject(configuration.generalities.subtitle)

	pptx.defineSlideMaster(getMasterSlideBaseOptions(configuration))
	pptx.defineSlideMaster(getMasterSlideDefaultOptions(configuration))
	pptx.defineSlideMaster(getMasterSlideTableOptions(configuration))
	pptx.defineSlideMaster(getMasterSlideChartOptions(configuration))
  pptx.defineSlideMaster(getMasterSlideTableWithSubtitleOptions(configuration))
  pptx.defineSlideMaster(getMasterSlideInfoOptions(configuration))

	const titleSection = createTitle(pptx, configuration)
	const summarySection = configuration.generalities.summary ? createSummary(pptx) : null
	const titles: string[] = []

	let sectionNumber = 0

  if (localAuthority.isDebtSimplified) {
    configuration.debt.annuity.tables = false;
    configuration.debt.annuity.PieChartType = false;
    configuration.debt.capitalReimbursement.tables = false;
    configuration.debt.capitalReimbursement.PieChartType = false;
    configuration.debt.interestReimbursement.tables = false;
    configuration.debt.interestReimbursement.PieChartType = false;
    configuration.debt.remainingCapital.tables = false;
    configuration.debt.remainingCapital.PieChartType = false;
  }
  
	if (configuration.generalities.glossary) {
		const glossary = await createGlossary(pptx, ++sectionNumber,gabarit)
    titles.push(glossary.title);
	}  

	if (checkObservationSection(configuration)) {
		const observationSection = await createObservationSection(
			pptx,
			configuration,
			[++sectionNumber],
			localAuthority,
			scenario,
			ppi,
			decomposedLoans.map(dl => dl.decomposition),
			loans,
			propsloan,
      gabarit=="DOB"
		)
		titles.push(observationSection.title)
	}

	if (DebtGenerator.checkDebtSection(configuration)) {
		const debtSection = DebtGenerator.createDebtSection(
			pptx,
			configuration,
			[++sectionNumber],
			decomposedLoans,
      scenario,
      ppi,
      loans,
      localAuthority
		)
		titles.push(debtSection.title)
	}

	if(budgetAnnexGenerator.checkBudgetSection(configuration)||
	   budgetAnnexGenerator.checkdettesReelles(configuration)||
	   budgetAnnexGenerator.checkdettesSimulées(configuration)||
	   budgetAnnexGenerator.checkdettesGlobales(configuration)) {
		const budgetSection = budgetAnnexGenerator.createBudgetSection(
			pptx,
			configuration,
			[++sectionNumber],
			localAuthority,
			scenario,
			ppi,
			decomposedLoans.map(dl => dl.decomposition),
			budgetData,
			childDecomposedLoans,
			decomposedLoans,
		)
		titles.push(budgetSection.title)
	}

	if (checkPpiSection(configuration)) {
		const ppiSection = createPpiSection(
			pptx,
			configuration,
			[++sectionNumber],
			localAuthority,
			ppi,
			decomposedLoans,
			scenario
		)
		titles.push(ppiSection.title)
	}

  if(DobSection.checkDobSection(configuration)){
    const dobSection = DobSection.createDobSection(
      pptx,
      configuration,
      [++sectionNumber],
      configuration.generalities.years,
      configuration.generalities.referenceYear,
      scenario,
			decomposedLoans.map(dl => dl.decomposition),
      localAuthority,
      loans,
      ppi
    );
    titles.push(dobSection.title)
  }


	if (summarySection) {
		summarySection.slides[0].addText(
			titles.map(t => ({ text: t, options: { breakLine: true }})),
				Constants.getTextPlacementOptions
		)
	}

  pptx.addNewSlide(MasterSlide.Info)

	return Promise.resolve(pptx)
}

/**
 * Creates the glossary slides and returns its title.
 * @param pptx
 * @param sectionNumber
 */
async function createGlossary(
  pptx: PptxGenJS,
  sectionNumber: number,
  gabarit:string
): Promise<DocumentSection> {
  const numbers = [sectionNumber];
  const slide = pptx.addNewSlide(MasterSlide.Default);
  const slides:Slide[] = [];
  slides.push(slide)

  const title = formatTitle("Glossaire", numbers);
  slide.addText(title, { placeholder: Placeholder.Title });

  const section = await createGlossaireSection(pptx,[++sectionNumber],gabarit)
  section.slides.forEach(s=>slides.push(s));
  return { slides: slides, title };
}

function createSummary(pptx: PptxGenJS): DocumentSection {
  const slide = pptx.addNewSlide(MasterSlide.Default);
  const title = "Table des matières";
  slide.addText(title, { placeholder: Placeholder.Title });
  return { slides: [slide], title };
}

function createTitle(
  pptx: PptxGenJS,
  configuration: ReportConfiguration
): DocumentSection {
  const slide = pptx.addNewSlide(MasterSlide.Base);
  const title = configuration.generalities.title || Constants.DEFAULT_DOC_TITLE;
  const logoH = toInches(5);
  const titleY = Constants.margins.top + toInches(2);
  const titleW =
    Constants.layout.w - Constants.margins.left - Constants.margins.right;
  const titleH = Constants.layout.middle - titleY;

  const logo = configuration.generalities.logo;
  if (configuration.generalities.logo) {
    slide.addImage({
      x: "62%",
      y: Constants.layout.middle + toInches(0.5),
      w: logo.width,
      h: logo.height,
      data: logo.data,
      sizing: { h: logoH, type: "contain", w: toInches(10.75) },
    });
  }

  slide.addText(title, {
    x: "60%",
    y: titleY,
    w: "35%",
    h: titleH,
    color: Constants.DEFAULT_TEXT_COLOR,
    fontSize: 32,
    fontFace: Constants.DEFAULT_FONT_FACE,
    valign: "bottom"
  })

  slide.addShape(pptx.shapes.LINE, {
    x: "60%",
    y: "50%",
    w: "35%",
    h: 0,
    lineSize: 1.5
  })

  if (configuration.generalities.subtitle) {
    slide.addText(configuration.generalities.subtitle, {
      x: Constants.margins.left,
      y: "50%",
      w: titleW,
      h: titleH,
      color: Constants.DEFAULT_TEXT_COLOR,
      fontFace: Constants.DEFAULT_FONT_FACE,
      fontSize: 24,
      valign: "top"
    })
  }

  return { slides: [slide], title }
}


// WEBPACK FOOTER //
// ./src/component/management-report/generator/index.ts